<script setup>
import { computed, ref, watch } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import groupContactsAPI from 'dashboard/api/groupContacts';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:show']);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const searchQuery = ref('');
const members = ref([]);
const currentPage = ref(1);
const totalCount = ref(0);
const isLoading = ref(false);
const hasLoadedOnce = ref(false);

const conversationMembers = computed(() => {
  return (props.conversation.group_contacts || [])
    .map(groupContact => groupContact.contact)
    .filter(Boolean);
});

const primaryContact = computed(() => props.contact || {});

const primaryMemberCount = computed(() => (primaryContact.value.id ? 1 : 0));

const effectiveTotalCount = computed(() => {
  return totalCount.value || conversationMembers.value.length;
});

const hasMorePages = computed(() => {
  return members.value.length < effectiveTotalCount.value;
});

const isSearching = computed(() => searchQuery.value.trim().length > 0);

const normalizedQuery = computed(() => searchQuery.value.toLowerCase().trim());

const primaryContactMatches = computed(() => {
  if (!isSearching.value) return true;

  const name = primaryContact.value.name?.toLowerCase() || '';
  const identifier = primaryContact.value.identifier?.toLowerCase() || '';
  return (
    name.includes(normalizedQuery.value) ||
    identifier.includes(normalizedQuery.value)
  );
});

const filteredMembers = computed(() => {
  if (!isSearching.value) {
    return members.value;
  }

  return members.value.filter(member => {
    const searchValues = [
      member.name,
      member.identifier,
      member.phone_number,
      member.email,
    ]
      .map(value => value?.toLowerCase() || '')
      .filter(Boolean);

    return searchValues.some(value => value.includes(normalizedQuery.value));
  });
});

const syncMembersFromConversation = () => {
  members.value = conversationMembers.value;
  currentPage.value = 1;
  totalCount.value = conversationMembers.value.length;
};

const fetchGroupContacts = async (page = 1) => {
  if (!props.conversation.id) return;

  isLoading.value = true;
  try {
    const response = await groupContactsAPI.getGroupContacts(props.conversation.id, {
      page,
    });
    const { meta = {}, payload = [] } = response.data || {};
    totalCount.value = meta.count || 0;
    currentPage.value = meta.current_page || page;

    if (page === 1) {
      members.value = payload;
    } else {
      members.value = [...members.value, ...payload];
    }
    hasLoadedOnce.value = true;
  } catch (error) {
    syncMembersFromConversation();
  } finally {
    isLoading.value = false;
  }
};

const loadMore = () => {
  if (!isLoading.value && hasMorePages.value) {
    fetchGroupContacts(currentPage.value + 1);
  }
};

const onClose = () => {
  localShow.value = false;
};

watch(
  conversationMembers,
  () => {
    syncMembersFromConversation();
  },
  { immediate: true }
);

watch(
  () => props.show,
  newValue => {
    if (!newValue) {
      searchQuery.value = '';
      return;
    }

    syncMembersFromConversation();
    if (!hasLoadedOnce.value || members.value.length === 0) {
      fetchGroupContacts(1);
    }
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <div class="flex flex-col h-auto max-h-[600px]">
      <woot-modal-header
        :header-title="$t('CONVERSATION.GROUP.MEMBERS')"
        :header-content="
          $t('CONVERSATION.GROUP.MEMBER_COUNT', {
            count: effectiveTotalCount + primaryMemberCount,
          })
        "
      />
      <div class="px-8 pb-8">
        <input
          v-model="searchQuery"
          type="text"
          class="w-full px-3 py-2 mb-4 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand"
          :placeholder="$t('CONVERSATION.GROUP.SEARCH_MEMBERS')"
        />

        <div class="max-h-[400px] overflow-y-auto space-y-2">
          <div
            v-if="
              isSearching &&
              !primaryContactMatches &&
              filteredMembers.length === 0
            "
            class="py-8 text-center text-n-slate-10"
          >
            {{ $t('CONVERSATION.GROUP.NO_MEMBERS_FOUND') }}
          </div>

          <div
            v-if="primaryContact.id && (!isSearching || primaryContactMatches)"
            class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1 border border-n-weak"
          >
            <Avatar
              :src="primaryContact.thumbnail"
              :name="primaryContact.name"
              :size="32"
              rounded-full
            />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1">
                <span class="block text-sm font-medium truncate text-n-slate-12">
                  {{ primaryContact.name }}
                </span>
                <span
                  v-tooltip="$t('CONVERSATION.GROUP.PRIMARY_CONTACT')"
                  class="i-lucide-star text-n-amber-9 text-xs"
                />
              </div>
              <span
                v-if="primaryContact.identifier"
                class="text-xs text-n-slate-10 truncate block"
              >
                {{ primaryContact.identifier }}
              </span>
            </div>
          </div>

          <template v-if="isLoading && !hasLoadedOnce">
            <div class="flex items-center justify-center py-8">
              <Spinner />
            </div>
          </template>
          <template v-else>
            <div
              v-for="member in filteredMembers"
              :key="member.id"
              class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1 border border-n-weak"
            >
              <Avatar
                :src="member.thumbnail"
                :name="member.name"
                :size="32"
                rounded-full
              />
              <div class="flex-1 min-w-0">
                <span class="block text-sm font-medium truncate text-n-slate-12">
                  {{ member.name }}
                </span>
                <span
                  v-if="member.identifier"
                  class="text-xs text-n-slate-10 truncate block"
                >
                  {{ member.identifier }}
                </span>
              </div>
            </div>
          </template>
        </div>

        <div
          v-if="hasMorePages && !isSearching"
          class="flex justify-center mt-4"
        >
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3 disabled:opacity-50"
            :disabled="isLoading"
            @click="loadMore"
          >
            <span v-if="isLoading" class="flex items-center gap-2">
              <Spinner size="small" />
              {{ $t('CONVERSATION.GROUP.LOAD_MORE') }}
            </span>
            <span v-else>{{ $t('CONVERSATION.GROUP.LOAD_MORE') }}</span>
          </button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
